/**
 * Shared assets for the phone-line board: a California map projection/outline
 * and the deterministic FAKED geo for the map. There is no real location data —
 * each active line is assigned a city + jittered dot position purely from its
 * stable `phone_line_id`, so the map is illustrative and a dot keeps its spot
 * across polls until that call ends.
 *
 * TODO(phone-lines): a future PR will derive each dot's location from the
 * dialed phone number's area code (real geography), and scale the map to the
 * smallest region that fits all active lines (Country -> State -> City) rather
 * than the fixed California outline below.
 */

export const MAP_VIEWBOX = '0 0 220 260'

// California bounding box the projection maps into the viewBox.
const LON_MIN = -124.5
const LON_MAX = -114.0
const LAT_MIN = 32.3
const LAT_MAX = 42.2
const W = 220
const H = 260
const MARGIN = 8

function proj(lon: number, lat: number): [number, number] {
  const x = MARGIN + ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * (W - 2 * MARGIN)
  const y = MARGIN + ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * (H - 2 * MARGIN)
  return [x, y]
}

type City = { name: string; lat: number; lon: number; w: number }

const CITIES: City[] = [
  { name: 'Los Angeles', lat: 34.0522, lon: -118.2437, w: 14 },
  { name: 'San Diego', lat: 32.7157, lon: -117.1611, w: 9 },
  { name: 'San Francisco', lat: 37.7749, lon: -122.4194, w: 8 },
  { name: 'San Jose', lat: 37.3382, lon: -121.8863, w: 7 },
  { name: 'Sacramento', lat: 38.5816, lon: -121.4944, w: 6 },
  { name: 'Fresno', lat: 36.7378, lon: -119.7871, w: 5 },
  { name: 'Long Beach', lat: 33.7701, lon: -118.1937, w: 4 },
  { name: 'Oakland', lat: 37.8044, lon: -122.2712, w: 4 },
  { name: 'Bakersfield', lat: 35.3733, lon: -119.0187, w: 4 },
  { name: 'Anaheim', lat: 33.8366, lon: -117.9143, w: 3 },
  { name: 'Riverside', lat: 33.9533, lon: -117.3962, w: 3 },
  { name: 'Santa Ana', lat: 33.7455, lon: -117.8677, w: 3 },
  { name: 'Stockton', lat: 37.9577, lon: -121.2908, w: 3 },
  { name: 'Fremont', lat: 37.5485, lon: -121.9886, w: 2 },
  { name: 'San Bernardino', lat: 34.1083, lon: -117.2898, w: 2 },
  { name: 'Modesto', lat: 37.6391, lon: -120.9969, w: 2 },
  { name: 'Oxnard', lat: 34.1975, lon: -119.1771, w: 2 },
  { name: 'Salinas', lat: 36.6777, lon: -121.6555, w: 2 },
  { name: 'Pasadena', lat: 34.1478, lon: -118.1445, w: 2 },
  { name: 'Palm Springs', lat: 33.8303, lon: -116.5453, w: 1 },
  { name: 'Redding', lat: 40.5865, lon: -122.3917, w: 1 },
  { name: 'Chico', lat: 39.7285, lon: -121.8375, w: 1 },
]

const CITY_CUM: number[] = []
let _wsum = 0
for (const c of CITIES) {
  _wsum += c.w
  CITY_CUM.push(_wsum)
}
const WEIGHT_SUM = _wsum

// California silhouette (lon, lat), clockwise from the NW corner. Enough points
// that the coast reads as California rather than a blocky polygon.
const OUTLINE: Array<[number, number]> = [
  [-124.21, 42.0], // NW corner (Oregon border)
  [-124.3, 41.2],
  [-124.36, 40.44], // Cape Mendocino
  [-124.11, 40.1],
  [-123.83, 39.35],
  [-123.72, 38.95],
  [-123.13, 38.45],
  [-122.95, 38.15],
  [-122.53, 37.78], // San Francisco
  [-122.4, 37.4],
  [-122.15, 36.95],
  [-121.9, 36.62], // Monterey
  [-121.28, 35.67], // Big Sur
  [-120.9, 35.25],
  [-120.63, 34.9], // Point Conception
  [-120.05, 34.46],
  [-119.25, 34.27], // Santa Barbara
  [-118.5, 34.02], // Santa Monica Bay
  [-118.15, 33.72],
  [-117.93, 33.6], // Orange County
  [-117.32, 33.05],
  [-117.13, 32.53], // San Diego / SW corner
  [-115.5, 32.62], // Mexico border
  [-114.72, 32.72], // SE corner (Colorado River / Yuma)
  [-114.68, 33.42],
  [-114.52, 33.69],
  [-114.63, 34.17],
  [-114.38, 34.3],
  [-114.26, 34.75],
  [-114.63, 35.0], // near Needles
  [-117.5, 37.22], // Nevada diagonal
  [-120.0, 39.0],
  [-120.0, 42.0], // NE corner
]

export const MAP_OUTLINE_PATH =
  'M ' +
  OUTLINE.map((p) => {
    const [x, y] = proj(p[0], p[1])
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' L ') +
  ' Z'

/** Deterministic pseudo-random in [0,1) from a numeric seed + salt. */
function rand01(n: number, salt: number): number {
  const x = Math.sin((n + 1) * 12.9898 + salt * 78.233) * 43758.5453
  return x - Math.floor(x)
}

/**
 * FNV-1a hash of a string → uint32, so a stable id can seed rand01.
 *
 * Exported because the duty colour map needs the same trick: turn a stable id into
 * a deterministic slot. Shared rather than duplicated so both call sites can't
 * drift onto different hashes.
 */
export function hashSeed(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export type DotPlacement = { city: string; x: number; y: number }

/**
 * Faked map placement for one active call — stable for a given stable id (e.g.
 * phone_line_id), NOT the positional line_number, so a dot keeps its spot when
 * other calls end and the ordinals shift. Picks a weighted California city,
 * then jitters within it.
 */
export function activeLinePlacement(id: string): DotPlacement {
  const seed = hashSeed(id)
  const r = rand01(seed, 1) * WEIGHT_SUM
  let idx = CITY_CUM.findIndex((cum) => r <= cum)
  if (idx < 0) idx = CITIES.length - 1
  // CITIES is a non-empty literal and idx is clamped in range.
  const city = CITIES[idx]!

  const [cx, cy] = proj(city.lon, city.lat)
  const angle = rand01(seed, 2) * Math.PI * 2
  const radius = Math.sqrt(rand01(seed, 3)) * (2 + city.w * 0.6)
  return {
    city: city.name,
    x: cx + Math.cos(angle) * radius,
    y: cy + Math.sin(angle) * radius,
  }
}
